import React, { useState, useEffect } from 'react';
import { AsyncCheckboxList } from '../Form/AsyncCheckboxList';
import { AsyncSelectFilter } from './AsyncSelectFilter';
import { BackButton } from '../BackButton';
import { BaseFilter } from './base-filter';
import { Button } from '../Button';
import { FAIcon } from '../fa-icon';
import { i18n } from '../../i18n/translate';
import { kebabCase } from 'lodash';
import { MaterialCheckbox } from '../../material/MaterialCheckbox';
import { noop } from '../../utils/noop';
import { onExecuteKeyDown } from '../../utils/KeyboardUtil';
import { selectComponents as selectWrapperComponents } from '../Form/Controls/SelectComponentWrappers';
import { Step } from '../Step';
import { Wizard } from '../Wizard';
import PropTypes from 'prop-types';
import Select, { components as selectComponents } from 'react-select';
import uniqBy from 'lodash/uniqBy';
import * as Core from '../../core';

const MAX_OPTIONS = 10;
const ClearIndicator = null;
const IndicatorSeparator = null;

export const IndicatorsContainer = ({ selectProps }) => {
  const formattedName = selectProps?.name?.replace?.('[]', '') ?? '';
  const name = formattedName ? `-${formattedName}` : '';
  const dataObjectId = `Select${name}-indicators`;

  return (
    <div className="filter-search-indicator" data-object-id={dataObjectId}>
      <FAIcon icon="search" />
    </div>
  );
};

export const SelectFilter = props => {
  const {
    asyncOptions = {},
    customComponent,
    defaultValue,
    explainerKey,
    iconFamily,
    iconKey,
    isAsync,
    isTwoStep,
    labelKey = 'label',
    onChange = noop,
    onClearFilters = noop,
    onMenuClose = noop,
    onMenuOpen = noop,
    options = [],
    selectedKey,
    showHelperText,
    showSelected,
    valueKey = 'id',
    asyncAccessible,
    ...selectProps
  } = props;

  const [value, setValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  // this is the ID of the category that's currently being shown, if any
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // this array holds a copy of the category object with the selected values for it
  // only categories that have options selected are included
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [asyncData, setAsyncData] = useState([]);
  const isSelectingCategory = isTwoStep && wizardStep === 0;

  useEffect(() => {
    if (selectProps.value) {
      if (isTwoStep) {
        // copy selected category values from props to state
        const newCategories = options.reduce((acc, option) => {
          const valueCategory = selectProps.value.find(val => val.id === option.id);
          if (valueCategory) {
            const values = [...valueCategory.values];
            acc.push({ ...valueCategory, values });
          }
          return acc;
        }, []);
        setSelectedCategories(newCategories);
      } else {
        setValue(selectProps.value);
      }
    }
  }, [selectProps.value, isTwoStep, options.length]);

  useEffect(() => {
    if (defaultValue) {
      if (isTwoStep) {
        // copy default category values from props to state
        // lookup and add any selected values in the category to the copied object
        const selectedCategories = defaultValue
          .map(cat => {
            const category = options.find(opt => opt.id === cat.id);
            if (category) {
              const values = cat.values.map(val => category.categoryOptions?.find(opt => opt.id === val.id));
              return { ...category, values };
            }
          })
          .filter(cat => cat);
        if (selectedCategories.length) {
          setSelectedCategories(selectedCategories);
          onChange(selectedCategories);
        }
      } else {
        setValue(defaultValue);
      }
    }
  }, [defaultValue, isTwoStep, options.length]);

  const performSetValue = val => {
    if (Array.isArray(val)) {
      val = val.filter(item => !(typeof item === 'object' && Object.keys(item).length === 0));
    }
    setValue(val);
    onChange(val);
  };

  const performSetCategoryValues = newValues => {
    const selectedCategory = getSelectedCategory();
    const newCategories = selectedCategories.filter(cat => cat.id !== selectedCategoryId);
    if (newValues.length > 0) {
      newCategories.push({ ...selectedCategory, values: newValues });
    }
    setValue(newCategories);
    setSelectedCategories(newCategories);
    onChange(newCategories);
  };

  const performTwoStepSetValue = (option, action) => {
    if (isSelectingCategory) {
      if (action === 'go-forward') {
        if (selectedCategoryId !== option.id) {
          setSelectedCategoryId(option.id);
        }
        setWizardStep(1);
      } else if (action === 'clear') {
        setSelectedCategories([]);
        onChange([]);
      }
    } else {
      let newValues;
      const selectedCategory = getSelectedCategory();
      const currentValues = selectedCategory.values || [];
      switch (action) {
        case 'deselect-option':
          newValues = currentValues.filter(val => val.id !== option.id);
          break;
        case 'select-option':
          if (!currentValues.includes(val => val.id === option.id)) {
            newValues = [...currentValues, option];
          } else {
            newValues = [...currentValues];
          }
          break;
        case 'clear':
          setWizardStep(0);
          newValues = [];
          break;
      }
      performSetCategoryValues(newValues);
    }
  };

  const onSelectChange = (val, action) => {
    if (customComponent) {
      const toggleVal =
        val.id === true
          ? { id: false, label: selectProps.uncheckedLabel || `${i18n.t('Filtering by')} ${selectProps.label}` }
          : { id: true, label: selectProps.checkedLabel || `${i18n.t('Filtering by')} ${selectProps.label}` };
      performSetValue(toggleVal);
      setIsOpen(true);
      return;
    }

    if (!selectProps.isMulti && selectProps.value === val) {
      performSetValue({});
      setIsOpen(false);
      return;
    }

    if (isTwoStep) {
      performTwoStepSetValue(action.option, action.action);
      if (!selectProps.isMulti) setIsOpen(false);
      return;
    }

    switch (action.action) {
      case 'select-option':
        performSetValue(val);
        break;
      case 'deselect-option':
        performSetValue(val);
        break;
      case 'clear':
        performSetValue(undefined);
        break;
    }

    if (!selectProps.isMulti) {
      setIsOpen(false);
    }
  };

  const findInAsyncData = keyField => {
    const selectValue = selectProps.value || value;
    const item = asyncData.find(item => String(item[valueKey]) === keyField);

    // if it's not in the asyncData yet, it means it comes pre-selected
    return item || selectValue.find(item => String(item[valueKey]) === keyField);
  };

  const onAsyncSelectChange = values => {
    const newValues = values.map(value => findInAsyncData(value));
    if (isTwoStep) {
      performSetCategoryValues(newValues);
    } else {
      performSetValue(newValues);
    }
  };

  const onToggleOpen = isOpen => {
    setIsOpen(isOpen);
    if (isOpen) {
      onMenuOpen();
    } else {
      onMenuClose();
    }
  };

  const renderClearFilters = (setValue = noop) => {
    const onClear = () => {
      onClearFilters();
      setValue(undefined, 'clear');
    };

    return (
      <>
        <Core.Divider />
        <Core.Layout mt="sm">
          <Button
            onClick={onClear}
            onKeyDown={onExecuteKeyDown(onClear)}
            style="tertiary"
            icon="xmark"
            label={i18n.t('Clear Filter')}
          />
        </Core.Layout>
      </>
    );
  };

  const Menu = props => (
    <selectComponents.Menu {...props}>
      <Core.Layout>{props.children} </Core.Layout>
      {props.hasValue && selectProps.clearable && renderClearFilters(props.setValue)}
    </selectComponents.Menu>
  );

  Menu.propTypes = {
    hasValue: PropTypes.bool,
    setValue: PropTypes.func,
  };

  const renderOptionLabel = data => {
    return (
      <>
        {iconKey && <FAIcon icon={data[iconKey]} />}
        <div
          className="option-container"
          data-object-type="menu-dropdown-options"
          data-action={`select-option-${kebabCase(data.label)}`}
          data-object-id={data.name}
        >
          <div className="option-label">{data[labelKey]}</div>
          {explainerKey && <div className="option-explainer">{data[explainerKey]}</div>}
        </div>
      </>
    );
  };

  const isClearableSelect = !(selectProps.clearable === false || selectProps.isClearable === false);
  const isSearchable = !!options && options.length > MAX_OPTIONS;
  const selectionVisible = !selectProps.isMulti && showSelected && !!value;
  const classPrefix = `Select${selectProps?.name ? `-${selectProps?.name}` : ''}`;

  const Option = props => {
    if (customComponent) {
      const CustomOptionComponent = customComponent.Option;
      return <CustomOptionComponent {...props} />;
    }

    if (isTwoStep) {
      if (isSelectingCategory) {
        const {
          children,
          data: { id },
        } = props;
        const selectedValues = selectedCategoryValues(id).length;
        return (
          <selectComponents.Option {...props}>
            <div className="option-text">{children}</div>
            <div className="option-right">
              {selectedValues > 0 && <span className="option-count">{selectedCategoryValues(id).length}</span>}
              <FAIcon icon="angle-right" />
            </div>
          </selectComponents.Option>
        );
      } else {
        const {
          selectProps: { value },
          data: { id },
        } = props;
        const isThisValueSelected = value.find(val => val.id === id);
        return <selectWrapperComponents.Option {...props} isSelected={!!isThisValueSelected} />;
      }
    }
    const helperText = showHelperText ? props.data?.helperText : null;
    return <selectWrapperComponents.Option {...props} helperText={helperText} />;
  };

  const getSelectedCategory = (id = selectedCategoryId) => {
    let selectedCategory = selectedCategories.find(cat => cat.id === id);
    if (!selectedCategory) {
      selectedCategory = options.find(opt => opt.id === id);
    }
    return selectedCategory;
  };

  const selectedCategoryValues = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.values) || [];
  };
  const selectedCategoryOptions = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.categoryOptions) || [];
  };
  const selectedCategoryAsyncOptions = (id = selectedCategoryId) => {
    const selectedCategory = getSelectedCategory(id);
    return (selectedCategory && selectedCategory.asyncOptions) || [];
  };

  const renderSelect = (options = [], isMulti = false, isAsync = false, asyncOptions = {}) => {
    const isOptionSelected = option => {
      if (isTwoStep && isSelectingCategory) {
        return selectedCategoryValues(option.id).length > 0;
      }
      if (!Array.isArray(value)) {
        if (typeof value?.[labelKey] === 'string' && value[labelKey] === option[labelKey]) {
          return (
            value[valueKey] === 'true' || (isMulti === false && (value[valueKey] === true || value[valueKey] === false))
          );
        } else {
          return value === option[valueKey];
        }
      }
      let valueObject = value;
      if (isTwoStep) {
        valueObject = value?.find(obj => obj[valueKey] === selectedCategoryId)?.values;
      }
      return valueObject?.some(elem => {
        const item = elem[valueKey] || elem;
        return item === option[valueKey];
      });
    };

    const onChange = (val, action) => {
      if (!isTwoStep || !isSelectingCategory || action.action === 'clear') {
        onSelectChange(val, action);
      } else {
        onSelectChange(action.option, { action: 'go-forward', option: action.option });
      }
    };

    let selectValue = selectProps.value || value;

    if (isTwoStep && isAsync) {
      const wrapperValues = selectProps.value || value;
      const currentCategoryValues = wrapperValues?.find(wValue => wValue?.id === selectedCategoryId);

      selectValue = currentCategoryValues?.values ?? currentCategoryValues;
    }

    const currentOptions = [...options];
    const components = {
      ...selectWrapperComponents,
      ClearIndicator, // null
      IndicatorsContainer,
      IndicatorSeparator, // null
      Menu,
      Option,
    };
    const styles = {
      control: (base, state) => ({
        ...base,
        ...(state.selectProps.isSearchable
          ? {}
          : {
              border: 0,
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              height: '1px',
              margin: '-1px',
              overflow: 'hidden',
              padding: 0,
              position: 'absolute',
              whiteSpace: 'nowrap',
              width: '1px',
            }),
      }),
    };

    const AsynSelect = asyncAccessible ? AsyncSelectFilter : AsyncCheckboxList;
    return isAsync ? (
      <AsynSelect
        {...selectProps}
        {...asyncOptions}
        CheckboxComponent={MaterialCheckbox}
        key={selectedCategoryId}
        labelKey={labelKey}
        onChange={onAsyncSelectChange}
        pageSize={asyncOptions.pageSize || MAX_OPTIONS}
        renderClearFilters={isClearableSelect ? renderClearFilters : null}
        searchWrapperClasses="filter-search"
        selectedValues={selectValue ? selectValue.map(val => val[valueKey]) : []}
        showSelectOptions={false}
        valueKey={valueKey}
        onDataLoaded={data => setAsyncData(uniqBy([...asyncData, ...data], valueKey))}
      />
    ) : (
      <Select
        {...selectProps}
        styles={styles}
        aria-labelledby={`${selectProps.name}-label`}
        autoFocus={true}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        className="filter-select"
        classNamePrefix="filter-select"
        components={!explainerKey ? components : { Control: () => null }}
        controlShouldRenderValue={false}
        formatOptionLabel={renderOptionLabel}
        inputId={`${classPrefix}-input`}
        instanceId={`${selectProps.name ? `${selectProps.name}-` : ''}value`}
        isClearable={isClearableSelect}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isOptionSelected={isOptionSelected}
        key={selectedCategoryId}
        menuIsOpen={true}
        name={selectProps.name}
        onChange={onChange}
        options={currentOptions ?? []}
        placeholder={isSearchable ? i18n.t('Search') : ''}
        tabSelectsValue={false}
        value={isTwoStep ? selectedCategoryValues() : selectValue}
        getOptionLabel={item => item[labelKey]}
        getOptionValue={item => item[valueKey]}
      />
    );
  };

  let wizardSteps;
  if (isTwoStep) {
    wizardSteps = [
      // First step: category selection
      <Step key="step-1">
        <div className="step-1">{renderSelect(options, true)}</div>
      </Step>,
      // Second step: value selection
      <Step key="step-2">
        {renderSelect(selectedCategoryOptions(), true, isAsync, selectedCategoryAsyncOptions())}
      </Step>,
    ];
  }

  return (
    <BaseFilter
      {...props}
      disabled={props.isDisabled}
      isOpen={isOpen}
      onToggleOpen={onToggleOpen}
      selected={(selectionVisible && value[selectedKey || labelKey]) || ''}
      selectedIcon={(selectionVisible && iconKey && value[iconKey]) || ''}
      selectedIconFamily={iconFamily || ''}
    >
      {isTwoStep ? (
        <div className="two-step">
          <h5 className="category-title">
            {!isSelectingCategory ? (
              <>
                <BackButton
                  action={() => {
                    setSelectedCategoryId(null);
                    setWizardStep(0);
                  }}
                />
                {getSelectedCategory().label}
              </>
            ) : (
              i18n.t('Select a Category')
            )}
          </h5>
          <Wizard
            wizardStep={wizardStep}
            initialStep={0}
            onCancel={noop}
            onComplete={noop}
            hasStepIndicator={false}
            hasButtons={false}
            overrideSteps={true}
          >
            {wizardSteps}
          </Wizard>
        </div>
      ) : (
        renderSelect(options, selectProps.isMulti, isAsync, asyncOptions)
      )}
    </BaseFilter>
  );
};

SelectFilter.propTypes = {
  asyncOptions: PropTypes.shape({
    authToken: PropTypes.string,
    dataUrl: PropTypes.string,
    queryFields: PropTypes.object,
    pageSize: PropTypes.number,
  }),
  children: PropTypes.node,
  clearable: PropTypes.bool,
  customComponent: PropTypes.object,
  data: PropTypes.object,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  explainerKey: PropTypes.string,
  iconFamily: PropTypes.string,
  iconKey: PropTypes.string,
  isAsync: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  innerProps: PropTypes.object,
  innerRef: PropTypes.object,
  isTwoStep: PropTypes.bool,
  labelKey: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  options: PropTypes.array,
  selected: PropTypes.string,
  selectedKey: PropTypes.string,
  selectProps: PropTypes.object,
  showHelperText: PropTypes.bool,
  showSelected: PropTypes.bool,
  valueKey: PropTypes.string,
};
